/**
 * IA Accordion — <ia-accordion>
 * --------------------------------------------------------------------
 * Progressive enhancement for native <details>/<summary> items: animates
 * open/close (height via the Web Animations API) and, when
 * data-single-open="true", closes the other items when one opens.
 * Without JavaScript the items still work as plain <details>.
 *
 * Markup (rendered by sections/ia-image-accordion.liquid):
 *   <ia-accordion data-single-open="true">
 *     <details data-ia-item>
 *       <summary data-ia-summary>…</summary>
 *       <div data-ia-panel>…</div>
 *     </details>
 *   </ia-accordion>
 */
if (!customElements.get('ia-accordion')) {
  class IaAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-ia-item]'));
      this.singleOpen = this.dataset.singleOpen === 'true';
      this.duration = parseInt(this.dataset.duration || '250', 10);
      this.items.forEach((item) => {
        const summary = item.querySelector('[data-ia-summary]');
        if (!summary) return;
        summary.addEventListener('click', (event) => this.onToggle(event, item));
      });
    }

    get reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onToggle(event, item) {
      event.preventDefault();
      if (item.dataset.animating === 'true') return;

      if (item.open) {
        this.close(item);
      } else {
        if (this.singleOpen) {
          this.items.forEach((other) => {
            if (other !== item && other.open) this.close(other);
          });
        }
        this.open(item);
      }
    }

    open(item) {
      const panel = item.querySelector('[data-ia-panel]');
      item.open = true;
      item.dataset.animating = 'true';
      if (!panel || this.reducedMotion) {
        item.dataset.animating = 'false';
        return;
      }
      const height = panel.scrollHeight;
      const animation = panel.animate(
        [{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }],
        { duration: this.duration, easing: 'ease' }
      );
      animation.onfinish = animation.oncancel = () => {
        item.dataset.animating = 'false';
      };
    }

    close(item) {
      const panel = item.querySelector('[data-ia-panel]');
      if (!panel || this.reducedMotion) {
        item.open = false;
        return;
      }
      item.dataset.animating = 'true';
      const height = panel.scrollHeight;
      const animation = panel.animate(
        [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
        { duration: this.duration, easing: 'ease' }
      );
      animation.onfinish = animation.oncancel = () => {
        item.open = false;
        item.dataset.animating = 'false';
      };
    }
  }

  customElements.define('ia-accordion', IaAccordion);
}
